import type {ReactElement} from 'react';
import React, {useEffect} from 'react';
import useDefaultPanelClose from '@Panels/hooks/useDefaultPanelClose';
import {Panel, PanelHeader} from '@Components/panel';
import {ItemAlignmentOption} from '@Components/poster-editor/components/poster-editing-side-panel/components/item-alignment-option';
import {useUpdateEffect} from 'ahooks';
import {TextVerticalAlignmentOption} from '@Components/poster-editor/components/poster-editing-side-panel/components/text-vertical-alignment-option';
import {updateOptionsPanelHeight} from '@Components/poster-editor/library/poster-editor-library';
import {TextSlideAlignmentOption} from '@Components/poster-editor/components/poster-editing-side-panel/components/text-slide-alignment-option';
import {TextSlideVerticalAlignmentOption} from '@Components/poster-editor/components/poster-editing-side-panel/components/text-slide-vertical-alignment-option';
import {MediaSlideAlignmentOption} from '@Components/poster-editor/components/poster-editing-side-panel/components/media-slide-alignment-option';
import {ContextualMenu} from '@Components/contextual-menu';
import {TranscriptVerticalAlignOption} from '@Components/poster-editor/components/poster-editing-side-panel/components/transcript-vertical-align-option/transcript-vertical-align-option';
import {useSingleActivePosterItemId} from '@Hooks/poster-editor/useSingleActivePosterItemId';
import {useGetSelectedSlideId} from '@Hooks/poster-editor/useGetSelectedSlideId';

interface PosterEditorItemAlignmentPanelProps {
  panelId: string;
}

export default function PosterEditorItemAlignmentPanel({...props}: PosterEditorItemAlignmentPanelProps): ReactElement {
  const onClose = useDefaultPanelClose(props.panelId);
  const activeItemId = useSingleActivePosterItemId();
  const activeItem = activeItemId ? window.posterEditor?.whiteboard?.getItemForId(activeItemId) : undefined;
  const activeSlideUID = useGetSelectedSlideId();
  const activeSlide = activeSlideUID && activeItem?.isSlideshow() ? activeItem.getSelectedSlide() : null;

  useUpdateEffect((): void => {
    onClose();
  }, [activeItemId]);

  useUpdateEffect((): void => {
    if (activeItem?.isSlideshow()) {
      onClose();
    }
  }, [activeSlideUID]);

  useEffect((): void => {
    if (activeItem?.isTableItem() || activeItem?.isTab()) {
      updateOptionsPanelHeight(props.panelId, '132px');
    } else if (activeItem?.isSlideshow() && activeSlide?.isMediaSlide()) {
      updateOptionsPanelHeight(props.panelId, '132px');
    } else if (activeItem?.isTranscript) {
      updateOptionsPanelHeight(props.panelId, '132px');
    } else {
      updateOptionsPanelHeight(props.panelId, '194px');
    }
  }, []);

  const getHeader = (): ReactElement => {
    return <PanelHeader onClose={onClose} title={window.i18next.t('pmwjs_alignment')} />;
  };

  const getContent = (): ReactElement => {
    if (activeItem?.isTranscript()) {
      return (
        <ContextualMenu id="item-mobile-alignment-panel" hasMobileModalPadding>
          <TranscriptVerticalAlignOption showLargeIcons />
        </ContextualMenu>
      );
    }
    return (
      <ContextualMenu id="item-mobile-alignment-panel" hasMobileModalPadding>
        {activeItem?.isText() || activeItem?.isTableItem() || activeItem?.isTab() ? (
          <>
            <ItemAlignmentOption text={activeItem?.isTableItem() ? window.i18next.t('pmwjs_alignment') : window.i18next.t('pmwjs_horizontal')} showLargeIcons />
            {activeItem?.isText() ? <TextVerticalAlignmentOption text={window.i18next.t('pmwjs_vertical')} showLargeIcons /> : null}
          </>
        ) : null}
        {activeItem?.isSlideshow() && activeItem.getSelectedSlide().isTextSlide() ? (
          <>
            <TextSlideAlignmentOption text={window.i18next.t('pmwjs_horizontal')} showLargeIcons />
            <TextSlideVerticalAlignmentOption text={window.i18next.t('pmwjs_vertical')} showLargeIcons />
          </>
        ) : null}
        {activeItem?.isSlideshow() && activeSlide?.isMediaSlide() ? <MediaSlideAlignmentOption showLargeIcons /> : null}
      </ContextualMenu>
    );
  };

  return <Panel panelHeader={getHeader()} panelContent={getContent()} />;
}
